import axios, { AxiosResponse } from 'axios';
import type {
	CreateTagRequest,
	MergeTagsRequest,
	Tag,
	TagListResponse,
	TagSearchParams,
	TagSuggestParams,
	UpdateTagRequest,
} from '../types/tags';

/**
 * Get authentication token from storage
 */
const getAuthToken = (): string | null => {
	return sessionStorage.getItem('auth_token');
};

/**
 * Base API URL configuration
 */
const getApiBaseUrl = () => {
	if (import.meta.env.DEV) {
		return '/api/v1';
	}
	const apiUrl = process.env.VITE_API_URL || import.meta.env.VITE_API_URL;
	return `${apiUrl}/api/v1` || 'http://localhost:3000/api/v1';
};

/**
 * Axios instance configured for tags API
 */
const tagsApiClient = axios.create({
	baseURL: `${getApiBaseUrl()}/composer`,
	timeout: 30000,
	headers: {
		'Content-Type': 'application/json',
	},
	withCredentials: true,
});

/**
 * Add auth token to requests
 */
tagsApiClient.interceptors.request.use(
	config => {
		const token = getAuthToken();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	error => {
		return Promise.reject(error);
	}
);

/**
 * Handle auth errors
 */
tagsApiClient.interceptors.response.use(
	response => response,
	error => {
		if (error.response?.status === 401) {
			// Clear auth data and redirect to login
			sessionStorage.removeItem('auth_token');
			sessionStorage.removeItem('auth_user');
			window.location.href = '/login';
		}
		return Promise.reject(error);
	}
);

/**
 * Tags API client methods
 */
export const tagsApi = {
	/**
	 * Search tags with fuzzy matching
	 */
	async searchTags(params: TagSearchParams = {}): Promise<Tag[]> {
		const response: AxiosResponse<Tag[]> = await tagsApiClient.get('/tags/search', {
			params,
		});
		return response.data;
	},

	/**
	 * Get tag suggestions based on usage
	 */
	async suggestTags(params: TagSuggestParams = {}): Promise<Tag[]> {
		const response: AxiosResponse<Tag[]> = await tagsApiClient.get('/tags/suggest', {
			params,
		});
		return response.data;
	},

	/**
	 * Create a new tag
	 */
	async createTag(data: CreateTagRequest): Promise<Tag> {
		const response: AxiosResponse<Tag> = await tagsApiClient.post('/tags', data);
		return response.data;
	},

	/**
	 * Get paginated list of all tags
	 */
	async listTags(page = 1, limit = 20): Promise<TagListResponse> {
		const response: AxiosResponse<TagListResponse> = await tagsApiClient.get('/tags', {
			params: { page, limit },
		});
		return response.data;
	},

	/**
	 * Update an existing tag
	 */
	async updateTag(tagId: string, data: UpdateTagRequest): Promise<Tag> {
		const response: AxiosResponse<Tag> = await tagsApiClient.patch(`/tags/${tagId}`, data);
		return response.data;
	},

	/**
	 * Merge two tags
	 */
	async mergeTags(data: MergeTagsRequest): Promise<Tag> {
		const response: AxiosResponse<Tag> = await tagsApiClient.post('/tags/merge', data);
		return response.data;
	},

	/**
	 * Soft delete a tag
	 */
	async deleteTag(tagId: string): Promise<void> {
		await tagsApiClient.delete(`/tags/${tagId}`);
	},

	/**
	 * Recompute usage counts for all tags
	 */
	async recomputeUsageCounts(): Promise<void> {
		await tagsApiClient.post('/tags/recompute-usage');
	},

	/**
	 * Get all tags for a specific workflow
	 */
	async getWorkflowTags(workflowId: string): Promise<Tag[]> {
		const response: AxiosResponse<Tag[]> = await tagsApiClient.get(
			`/workflows/${workflowId}/tags`
		);
		return response.data;
	},

	/**
	 * Attach a tag to a workflow
	 */
	async attachTagToWorkflow(workflowId: string, tagId: string): Promise<void> {
		await tagsApiClient.post(`/workflows/${workflowId}/tags/${tagId}`);
	},

	/**
	 * Detach a tag from a workflow
	 */
	async detachTagFromWorkflow(workflowId: string, tagId: string): Promise<void> {
		await tagsApiClient.delete(`/workflows/${workflowId}/tags/${tagId}`);
	},
};

/**
 * React Query keys for tags
 */
export const tagsQueryKeys = {
	all: ['tags'] as const,
	search: (query: string) => ['tags', 'search', query] as const,
	suggest: (limit?: number) => ['tags', 'suggest', limit] as const,
	list: (page: number, limit: number) => ['tags', 'list', page, limit] as const,
	workflow: (workflowId: string) => ['tags', 'workflow', workflowId] as const,
};
